import React from "react";
import Section from "@/components/foundation/Section";
import Card from "@/components/foundation/Card";
import FadeIn from "@/components/foundation/FadeIn";
import Badge from "@/components/foundation/Badge";
import GlowOrb from "@/components/foundation/GlowOrb";
import ZenScoreGauge from "@/components/marketing/ZenScoreGauge";
import Sparkline from "@/components/marketing/Sparkline";
import {
  Activity,
  Repeat,
  TrendingUp,
  LineChart,
  Calendar,
  AlertTriangle,
} from "lucide-react";

const MINI_METRICS = [
  {
    label: "Monthly Revenue",
    delta: "+18% MoM",
    accent: true,
    data: [16, 18, 17, 22, 24, 27, 29, 32, 34, 38, 41, 44],
  },
  {
    label: "Deposit Consistency",
    delta: "Excellent",
    accent: true,
    data: [50, 52, 51, 50, 52, 53, 52, 51, 53, 52, 51, 52],
  },
  {
    label: "Avg Daily Balance",
    delta: "$42,180",
    accent: false,
    data: [22, 24, 26, 25, 28, 30, 32, 33, 35, 36, 38, 40],
  },
  {
    label: "Cash Flow Stability",
    delta: "Very Stable",
    accent: true,
    data: [40, 42, 41, 43, 42, 44, 43, 42, 43, 42, 44, 43],
  },
];

const SIGNALS = [
  {
    icon: Activity,
    title: "Real-Time Cash Flow",
    body: "Continuous view of inflows and outflows straight from your business bank.",
  },
  {
    icon: Repeat,
    title: "Deposit Patterns",
    body: "Regularity, sources, and consistency of customer payments over time.",
  },
  {
    icon: TrendingUp,
    title: "Revenue Growth",
    body: "Month-over-month trend velocity and directional signal quality.",
  },
  {
    icon: LineChart,
    title: "Balance Trends",
    body: "Average daily balance movement and available liquidity profile.",
  },
  {
    icon: Calendar,
    title: "Business Longevity",
    body: "Operating tenure, transaction depth, and account maturity signals.",
  },
  {
    icon: AlertTriangle,
    title: "Risk Signals",
    body: "Volatility, overdraft frequency, and other early-warning indicators.",
  },
];

export const ZenScoreSection = () => {
  return (
    <div
      id="zenscore-preview"
      className="relative overflow-hidden scroll-mt-24"
      data-testid="zenscore-section"
    >
      <GlowOrb
        color="emerald"
        size={600}
        intensity={0.14}
        className="-top-40 left-1/2 -translate-x-1/2"
        animate={false}
      />

      <Section
        eyebrow="Meet ZenScore"
        headline={
          <>
            Your business has a score. It just{" "}
            <span className="text-brand-emerald">isn&rsquo;t based on credit.</span>
          </>
        }
        subheadline="ZenScore is ZenMe's proprietary AI-powered funding intelligence engine. It analyzes your real business performance — cash flow, deposits, balances, growth — to reveal your true funding readiness."
        spacing="lg"
      >
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
          {/* LEFT — Score card */}
          <div className="lg:col-span-6">
            <Card
              variant="glass"
              interactive={false}
              className="relative flex flex-col p-8 sm:p-10"
              data-testid="zenscore-live-preview-mock"
            >
              <div className="flex items-center justify-between">
                <Badge variant="emerald" pulse data-testid="zenscore-live-badge">
                  Live Preview
                </Badge>
                <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-text-muted">
                  Session · demo
                </span>
              </div>

              <div className="flex justify-center pt-8 pb-4">
                <ZenScoreGauge
                  value={782}
                  size={260}
                  caption="Strong Funding Readiness"
                  data-testid="zenscore-main-gauge"
                />
              </div>

              <div className="mt-6 border-t border-white/[0.06] pt-6">
                <ul className="flex flex-col gap-4">
                  {MINI_METRICS.map((m) => (
                    <li
                      key={m.label}
                      className="flex items-center justify-between gap-4"
                    >
                      <span className="text-sm text-text-secondary">
                        {m.label}
                      </span>
                      <div className="flex flex-1 items-center justify-end gap-4">
                        <Sparkline
                          data={m.data}
                          width={100}
                          height={26}
                          stroke="#00E676"
                        />
                        <span
                          className={
                            m.accent
                              ? "min-w-[92px] text-right text-sm font-medium tabular-nums text-brand-emerald"
                              : "min-w-[92px] text-right text-sm font-medium tabular-nums text-white"
                          }
                        >
                          {m.delta}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </Card>
          </div>

          {/* RIGHT — Signals grid */}
          <div className="lg:col-span-6">
            <FadeIn>
              <h3 className="font-display text-2xl font-semibold text-white sm:text-3xl">
                What ZenScore analyzes
              </h3>
              <p className="mt-2 text-sm text-text-secondary">
                Six live signals that continuously refine your funding readiness picture.
              </p>
            </FadeIn>

            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {SIGNALS.map((s, i) => {
                const Icon = s.icon;
                return (
                  <FadeIn key={s.title} delay={0.05 * i}>
                    <Card
                      variant="flat"
                      className="group flex h-full min-h-[140px] flex-col justify-between p-5"
                      data-testid={`zenscore-signal-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-brand-emerald/25 bg-brand-emerald/8 text-brand-emerald transition-all duration-300 group-hover:scale-105 group-hover:shadow-[0_0_16px_rgba(0,230,118,0.35)]">
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="mt-4">
                        <h4 className="text-sm font-semibold text-white">
                          {s.title}
                        </h4>
                        <p className="mt-1 text-xs leading-relaxed text-text-secondary">
                          {s.body}
                        </p>
                      </div>
                    </Card>
                  </FadeIn>
                );
              })}
            </div>
          </div>
        </div>

        <FadeIn>
          <p className="mx-auto mt-16 max-w-3xl text-center text-xs italic leading-relaxed text-text-muted">
            ZenScore is not a credit score. It is a business health indicator
            built from real financial activity and continuously improves as
            our underwriting models evolve.
          </p>
        </FadeIn>
      </Section>
    </div>
  );
};

export default ZenScoreSection;
